import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import { analytics } from '../../services/analytics';
import { logToNewRelic } from '../../services/debug';
import { stores } from '../../stores/useStores';
import { getErrorMessage } from '../../utils/errors';

import { ErrorMessage } from './ErrorMessage';

interface Props {
  children?: ReactNode;
  portalVisible?: boolean;
}

interface State {
  hasError: boolean;
  shownAsPortal: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    shownAsPortal: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: !!error,
      shownAsPortal: !stores?.portalStore?.isVisible,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.state.shownAsPortal) {
      stores?.uiStore.closeFullPageDialog();
      stores?.uiStore.setCloseDialog();
      stores?.portalStore.openPortal(() => <ErrorMessage />, {
        persistent: true,
        silent: true,
        beforeClose: () => {
          this.setState({ shownAsPortal: false, hasError: false });
        },
      });
    }
    void analytics.sendEvent('error', {
      error_type: error.message,
    });

    const errorMessage = getErrorMessage(error);
    console.error(`Uncaught error, message: ${errorMessage}, info: ${JSON.stringify(errorInfo)}`);
    // The cause as attributes, so the JavaScriptError event can be queried by what actually crashed.
    void logToNewRelic('ErrorBoundary', { error, errorInfo }, 'error', {
      causeName: error.name,
      causeMessage: error.message.slice(0, 300),
      causeStack: error.stack?.slice(0, 1000),
    });
  }

  public render() {
    if (this.state.hasError) {
      return this.state.shownAsPortal ? null : <ErrorMessage />;
    }
    return this.props.children;
  }
}
